import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { fetchTeamReport } from "@/api/reports";
import { downloadFile } from "@/api/download";
import { Badge, Button, Card } from "@/components";
import { toast } from "@/state/toast.store";
import { useLang } from "@/i18n/LanguageContext";

export function TeamReportPage() {
  const { t } = useLang();
  const report = useQuery({ queryKey: ["team-report"], queryFn: ({ signal }) => fetchTeamReport(signal) });

  async function download(path: string, name: string) {
    try {
      await downloadFile(path, name);
    } catch (e) {
      toast.err(e instanceof Error ? e.message : t("Download mislukt", "Download failed"));
    }
  }

  return (
    <section>
      <div className="wrap">
        <div className="toolbar">
          <div>
            <p className="eyebrow">{t("Voortgang", "Progress")}</p>
            <h2 style={{ fontSize: 30 }}>{t("Teamoverzicht", "Team overview")}</h2>
          </div>
          <div className="spacer" />
          <Button variant="outline" onClick={() => download("/api/reports/team.csv", "team-overview.csv")}>{t("Export CSV", "Export CSV")}</Button>
          <Button variant="outline" onClick={() => download("/api/reports/team.pdf", "team-overview.pdf")}>{t("Export PDF", "Export PDF")}</Button>
        </div>

        {report.isLoading ? (
          <p>{t("Laden…", "Loading…")}</p>
        ) : report.data && report.data.members.length > 0 ? (
          <>
            <div className="stat-cards">
              <div className="stat-card"><div className="n">{report.data.totals.people}</div><div className="l">{t("Medewerkers", "People")}</div></div>
              <div className="stat-card"><div className="n">{report.data.totals.avg_percent}%</div><div className="l">{t("Gemiddelde voortgang", "Average progress")}</div></div>
              <div className="stat-card"><div className="n">{report.data.totals.fully_done}</div><div className="l">{t("Volledig afgerond", "Fully completed")}</div></div>
            </div>

            <Card>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t("Medewerker", "Person")}</th>
                    <th>{t("Functie", "Job title")}</th>
                    <th>{t("Afdeling", "Department")}</th>
                    <th>{t("Voortgang", "Progress")}</th>
                    <th>{t("Open verplicht", "Required open")}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {report.data.members.map((m) => (
                    <tr key={m.user_id}>
                      <td>
                        <strong>{m.full_name}</strong>
                        {m.role === "manager" && <Badge tone="neutral">{t("manager", "manager")}</Badge>}
                        <div style={{ color: "var(--grey-text)", fontSize: 12.5 }}>{m.email}</div>
                      </td>
                      <td>{m.job_title || "—"}</td>
                      <td>{m.department || "—"}</td>
                      <td>
                        <span className="mini-bar"><span className="mini-fill" style={{ width: `${m.percent}%` }} /></span>
                        <span className="ef-mono">{m.percent}%</span>
                        <div style={{ color: "var(--grey-text)", fontSize: 12 }}>{m.completed}/{m.total} {t("stappen", "steps")}</div>
                      </td>
                      <td>{m.outstanding > 0 ? <Badge tone="warn">{m.outstanding}</Badge> : <Badge tone="ok">0</Badge>}</td>
                      <td><Link to={`/app/employees/${m.user_id}/program`} style={{ color: "var(--amber-dark)", fontWeight: 600, fontSize: 13 }}>{t("traject", "journey")} ↗</Link></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </>
        ) : (
          <p className="no-data-message">{t("Nog geen medewerkers met een programma.", "No people with a program yet.")}</p>
        )}
      </div>
    </section>
  );
}
